import { useCallback, useEffect, useState } from 'react';
import { useServiceStore } from '../../../store/useServiceStore';
import { usePagedList } from '../../../hooks/usePagedList';
import { useUrlListState } from '../../../hooks/useUrlListState';

/** Search, status filter and page of the service list, kept in the URL (FAFD-50). */
export function useServiceList() {
  const { services: allServices, searchResults, loading, error, fetchServices, searchServices } =
    useServiceStore();
  const [listState, setListState] = useUrlListState(
    { q: '', status: 'all', page: 1 },
    { status: ['all', 'active', 'inactive'] },
  );
  const { q: appliedSearch, status: filterStatus } = listState;
  const services = appliedSearch ? searchResults : allServices;
  // The search runs server-side on Enter, so the URL keeps the applied term, not the draft.
  const [searchTerm, setSearchTerm] = useState(appliedSearch);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Every reload (search, save, retry) honours the applied search, so the list never drifts
  // from the term in the URL and the search box.
  const reloadServices = useCallback(
    () => (appliedSearch ? searchServices(appliedSearch) : fetchServices()),
    [appliedSearch, searchServices, fetchServices],
  );

  // The applied search drives the fetch, whether it came from Enter or from the URL (a sidebar
  // click clears it); the input follows so it never shows a term the list isn't filtered by.
  useEffect(() => {
    setSearchTerm(appliedSearch);
    reloadServices().then(() => setInitialLoadComplete(true));
  }, [appliedSearch, reloadServices]);

  const handleSearch = () => {
    // Enter on the term already applied searches again; the effect only runs on a change.
    if (searchTerm === appliedSearch) reloadServices();
    setListState({ q: searchTerm });
  };

  const filteredServices = services.filter((service) => {
    if (filterStatus === 'active' && service.status !== 'Active') return false;
    if (filterStatus === 'inactive' && service.status === 'Active') return false;
    return true;
  });

  const paged = usePagedList(filteredServices, undefined, [
    listState.page,
    (nextPage) => setListState({ page: nextPage }),
  ]);

  return {
    loading, error, initialLoadComplete, reloadServices,
    searchTerm, setSearchTerm, handleSearch,
    filterStatus, setFilterStatus: (status: string) => setListState({ status }),
    filteredServices, ...paged,
  };
}
